import { logger, Tooltip } from '@/ui'
import { useAtomValue, useSetAtom } from 'jotai'
import { PlusIcon } from 'lucide-react'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	clearSelectionAtom,
	getGroupSelectionState,
	selectAllAtom,
	selectedCandidateIdsAtom,
	toggleCandidateSelectionAtom,
} from '../../../selection-atoms'
import { CARD_WIDTH } from '../constants'
import { useAutoScroll } from '../hooks/use-auto-scroll'
import { useDragMonitoring } from '../hooks/use-drag-monitoring'
import type { Candidate, KanbanBoardProps } from '../types'
import { Column } from './column'
import { DraggableCard } from './draggable-card'
import { RejectedDrawer } from './rejected-drawer'

export function KanbanBoard<T extends Candidate>({
	candidates,
	stages,
	renderCard,
	sortFn,
	rejectedSortFn,
	onCandidateMove,
	onStagesChange,
	getCandidateId,
	getCandidateStage,
	onCardClick,
	getCandidateSelectLabel,
	rejectedStageId,
	editMode = false,
	onStageRename,
	onStageAdd,
	onStageDelete,
}: KanbanBoardProps<T>) {
	const { t } = useTranslation('common')
	const selectLabelFor = useCallback(
		(candidate: T) => getCandidateSelectLabel?.(candidate) ?? t('candidates.selectCandidate'),
		[getCandidateSelectLabel, t],
	)
	const selectedIds = useAtomValue(selectedCandidateIdsAtom)
	const toggleSelect = useSetAtom(toggleCandidateSelectionAtom)
	const selectAllInStage = useSetAtom(selectAllAtom)
	const clearSelection = useSetAtom(clearSelectionAtom)

	const columnReorderMode = editMode

	const [draggingId, setDraggingId] = useState<string | null>(null)
	const [draggingColumnId, setDraggingColumnId] = useState<string | null>(null)
	const [newlyAddedAfterStageId, setNewlyAddedAfterStageId] = useState<string | null>(null)

	// Maps each candidate to its current stage so a bulk drop can skip candidates
	// already in the target stage (no-op moves) instead of bailing out entirely.
	const candidateStages = useMemo(
		() => new Map(candidates.map(candidate => [getCandidateId(candidate), getCandidateStage(candidate)])),
		[candidates, getCandidateId, getCandidateStage],
	)

	useDragMonitoring({
		selectedIds,
		stages,
		candidateStages,
		onCandidateMove,
		onStagesChange,
		onCandidateDragStart: setDraggingId,
		onCandidateDragEnd: () => setDraggingId(null),
		onColumnDragStart: setDraggingColumnId,
		onColumnDragEnd: () => setDraggingColumnId(null),
		onClearSelection: () => clearSelection(),
		onMultiSelectDragStart: (candidateCount) => {
			const sourceStageIds = new Set(
				candidates
					.filter(candidate => selectedIds.has(getCandidateId(candidate)))
					.map(candidate => getCandidateStage(candidate)),
			)

			logger.track('Candidates: Initiated Bulk Move', {
				data: {
					trigger: 'kanban_drag',
					candidate_count: candidateCount,
					source_stages: stages
						.filter(stage => sourceStageIds.has(stage.id))
						.map(stage => stage.name),
				},
			})
		},
	})

	useAutoScroll()

	const handleCardClick = (candidate: T) => {
		if (onCardClick) {
			onCardClick(candidate)
		}
	}

	const handleSelectAllInStage = useCallback((candidateIds: string[], stageName: string) => {
		// selectAllAtom toggles: when every id is already selected this deselects the
		// stage. Only track the select action so deselects don't skew the metric.
		const isDeselecting = candidateIds.length > 0 && candidateIds.every(id => selectedIds.has(id))
		selectAllInStage(candidateIds)
		if (!isDeselecting) {
			logger.track('Candidates: Selected Entire Stage', {
				data: {
					stage_name: stageName,
					candidate_count: candidateIds.length,
				},
			})
		}
	}, [selectAllInStage, selectedIds])

	/**
	 * Handle adding a new stage - immediately adds and auto-focuses
	 */
	const handleAddStage = (afterStageId: string) => {
		if (onStageAdd) {
			onStageAdd(afterStageId, 'New Stage')
			setNewlyAddedAfterStageId(afterStageId)
		}
	}

	/**
	 * Group candidates by stage and sort them
	 * Excludes rejected candidates (handled separately)
	 */
	const groupedCandidates = useMemo(() =>
		stages.reduce(
			(acc, stage) => {
				acc[stage.id] = candidates
					.filter((c) => getCandidateStage(c) === stage.id)
					.sort(sortFn)
				return acc
			},
			{} as Record<string, T[]>,
		), [candidates, stages, getCandidateStage, sortFn])

	/**
	 * Separate rejected candidates with custom sorting
	 * Uses rejectedSortFn if provided, otherwise falls back to sortFn
	 */
	const rejectedCandidates = useMemo(() =>
		rejectedStageId
			? candidates
				.filter((c) => getCandidateStage(c) === rejectedStageId)
				.sort(rejectedSortFn || sortFn)
			: [], [candidates, rejectedStageId, getCandidateStage, rejectedSortFn, sortFn])

	const rejectedCandidateIds = rejectedCandidates.map(getCandidateId)
	const rejectedSelection = getGroupSelectionState(rejectedCandidateIds, selectedIds)

	return (
		<div
			className="grid h-full"
			style={{ '--card-width': CARD_WIDTH } as React.CSSProperties}
		>
			{/* Main content area with columns and rejected drawer */}
			<div className="flex-1 flex flex-col overflow-hidden">
				{/* Scrollable columns area */}
				<div className="flex-1 overflow-x-auto overflow-y-hidden p-4">
					<div className="flex gap-0 h-full min-w-min w-min shadow-md shadow-purple-400/30 rounded-md">
						{stages.map((stage, index) => {
							const prevStage = stages[index - 1]
							// Show add button if: onStageAdd is defined, in edit mode, not first column, previous stage is not locked, current stage is not "New"
							const showAddButton = editMode && onStageAdd && index > 0 && !prevStage?.locked
								&& stage.name !== 'New'

							// Check if this stage was just added (it appears after newlyAddedAfterStageId)
							const isNewlyAdded = newlyAddedAfterStageId && prevStage?.id === newlyAddedAfterStageId

							const stageCandidateIds = (groupedCandidates[stage.id] ?? []).map(getCandidateId)
							const stageSelection = getGroupSelectionState(stageCandidateIds, selectedIds)

							return (
								<div
									key={stage.id}
									className={`flex ${index > 0 && !showAddButton ? 'border-l border-gray-200' : ''}`}
								>
									{/* Add stage button before this column */}
									{showAddButton && (
										<div className="flex items-start pt-4">
											<Tooltip content="Add stage">
												<button
													onClick={() => handleAddStage(prevStage.id)}
													className="w-8 h-8 flex items-center justify-center rounded-full bg-purple-100 text-purple-600 hover:bg-purple-200 transition-colors mx-1"
													aria-label={t('pipeline.addStage')}
												>
													<PlusIcon className="size-5" />
												</button>
											</Tooltip>
										</div>
									)}
									<Column
										id={stage.id}
										name={stage.name}
										count={groupedCandidates[stage.id]?.length || 0}
										columnReorderMode={columnReorderMode}
										isDraggable={columnReorderMode && !stage.locked && index !== 0
											&& index !== stages.length - 1}
										isDraggingColumn={draggingColumnId === stage.id}
										editMode={editMode}
										locked={stage.locked}
										onNameChange={(newName) => {
											onStageRename?.(stage.id, newName)
											// Clear the auto-focus flag after name is changed
											if (isNewlyAdded) {
												setNewlyAddedAfterStageId(null)
											}
										}}
										onDelete={() => onStageDelete?.(stage.id)}
										autoFocusName={!!isNewlyAdded}
										onSelectAll={() => handleSelectAllInStage(stageCandidateIds, stage.name)}
										selectAllChecked={stageSelection.checked}
										selectAllIndeterminate={stageSelection.indeterminate}
									>
										{groupedCandidates[stage.id]?.map((candidate) => {
											const id = getCandidateId(candidate)
											const isBeingDragged = draggingId === id
												|| (draggingId !== null && selectedIds.has(draggingId)
													&& selectedIds.has(id))
											return (
												<DraggableCard
													key={id}
													candidateId={id}
													sourceStage={stage.id}
													selected={selectedIds.has(id)}
													selectedCount={selectedIds.size}
													onSelect={() => toggleSelect(id)}
													onClick={() => handleCardClick(candidate)}
													isDragging={isBeingDragged}
													disabled={columnReorderMode}
													selectLabel={selectLabelFor(candidate)}
												>
													{renderCard(candidate)}
												</DraggableCard>
											)
										})}
									</Column>
								</div>
							)
						})}
					</div>
				</div>

				{/* Rejected candidates drawer at bottom */}
				{rejectedStageId && (
					<RejectedDrawer
						candidates={rejectedCandidates}
						renderCard={renderCard}
						getCandidateId={getCandidateId}
						selectedIds={selectedIds}
						toggleSelect={toggleSelect}
						handleCardClick={handleCardClick}
						draggingId={draggingId}
						rejectedStageId={rejectedStageId}
						selectLabelFor={selectLabelFor}
						onSelectAll={() => handleSelectAllInStage(rejectedCandidateIds, 'Rejected')}
						selectAllChecked={rejectedSelection.checked}
						selectAllIndeterminate={rejectedSelection.indeterminate}
					/>
				)}
			</div>
		</div>
	)
}
